import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { HomeFooter } from '../components/home/HomeFooter';
import { HomeHeader } from '../components/home/HomeHeader';
import { loadAuthSession } from '../data/authSession';
import {
  searchBands,
  REGION_LABELS,
  GENRE_LABELS,
  type BandResponse,
  type Genre,
  type Region,
} from '../api/bandworld';

const REGION_OPTIONS = Object.entries(REGION_LABELS) as [Region, string][];
const GENRE_OPTIONS = Object.entries(GENRE_LABELS) as [Genre, string][];

export function BandsPage() {
  const navigate = useNavigate();
  const isAuthenticated = Boolean(loadAuthSession());

  // 필터 입력 상태
  const [regionInput, setRegionInput] = useState<Region | ''>('');
  const [genreInput, setGenreInput] = useState<Genre | ''>('');
  const [nameInput, setNameInput] = useState('');

  // 적용된(committed) 필터 — 검색 버튼을 눌러야 실제 fetch에 반영
  const [region, setRegion] = useState<Region | undefined>();
  const [genre, setGenre] = useState<Genre | undefined>();
  const [name, setName] = useState<string | undefined>();

  const [bands, setBands] = useState<BandResponse[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 필터가 바뀔 때 목록 초기화 후 첫 페이지 로드
  const filterKey = `${region}|${genre}|${name}`;
  const filterKeyRef = useRef(filterKey);

  useEffect(() => {
    let cancelled = false;
    filterKeyRef.current = filterKey;
    setBands([]);
    setNextCursor(null);
    setHasNext(false);
    setError(null);
    setLoading(true);

    searchBands({ region, genre, name: name || undefined, size: 20 })
      .then((res) => {
        if (cancelled) return;
        setBands(res.items);
        setNextCursor(res.nextCursor);
        setHasNext(res.hasNext);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message ?? '밴드 목록을 불러오지 못했습니다.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const handleSearch = () => {
    setRegion(regionInput || undefined);
    setGenre(genreInput || undefined);
    setName(nameInput.trim() || undefined);
  };

  const handleLoadMore = () => {
    if (!hasNext || !nextCursor || loadingMore) return;
    let cancelled = false;
    setLoadingMore(true);

    searchBands({ region, genre, name: name || undefined, cursor: nextCursor, size: 20 })
      .then((res) => {
        if (cancelled) return;
        setBands((prev) => [...prev, ...res.items]);
        setNextCursor(res.nextCursor);
        setHasNext(res.hasNext);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message ?? '더 불러오지 못했습니다.');
      })
      .finally(() => {
        if (!cancelled) setLoadingMore(false);
      });

    return () => {
      cancelled = true;
    };
  };

  return (
    <main className="bands-page">
      <HomeHeader authenticated={isAuthenticated} onGuestCta={() => navigate('/login')} variant="icon" />

      <section className="bands-page__inner">
        <h1 className="bands-page__title">밴드 찾기</h1>

        <div className="bands-page__filters">
          <select
            className="bands-page__filter-select"
            onChange={(e) => setRegionInput(e.target.value as Region | '')}
            value={regionInput}
          >
            <option value="">지역 전체</option>
            {REGION_OPTIONS.map(([code, label]) => (
              <option key={code} value={code}>{label}</option>
            ))}
          </select>

          <select
            className="bands-page__filter-select"
            onChange={(e) => setGenreInput(e.target.value as Genre | '')}
            value={genreInput}
          >
            <option value="">장르 전체</option>
            {GENRE_OPTIONS.map(([code, label]) => (
              <option key={code} value={code}>{label}</option>
            ))}
          </select>

          <input
            className="bands-page__filter-input"
            onChange={(e) => setNameInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
            placeholder="밴드 이름 검색"
            type="text"
            value={nameInput}
          />

          <button className="bands-page__filter-btn" onClick={handleSearch} type="button">
            검색
          </button>
        </div>

        {loading ? (
          <div className="bands-page__loading">로딩 중...</div>
        ) : error ? (
          <div className="bands-page__error">{error}</div>
        ) : bands.length === 0 ? (
          <div className="bands-page__empty">조건에 맞는 밴드가 없어요.</div>
        ) : (
          <>
            <ul className="bands-page__list">
              {bands.map((band) => (
                <li key={band.bandId}>
                  <Link className="bands-page__card" to={`/bands/${band.bandId}`}>
                    {band.imageUrl ? (
                      <img
                        alt={band.name}
                        className="bands-page__card-thumb"
                        src={band.imageUrl}
                      />
                    ) : null}
                    <h2 className="bands-page__card-name">{band.name}</h2>
                    <div className="bands-page__card-meta">
                      {band.region ? (
                        <span className="bands-page__card-chip">
                          {REGION_LABELS[band.region] ?? band.region}
                        </span>
                      ) : null}
                      {band.genre ? (
                        <span className="bands-page__card-chip">
                          {GENRE_LABELS[band.genre] ?? band.genre}
                        </span>
                      ) : null}
                      <span>멤버 {band.memberCount}/{band.capacity}명</span>
                    </div>
                    {band.introduction ? (
                      <p className="bands-page__card-intro">{band.introduction}</p>
                    ) : null}
                  </Link>
                </li>
              ))}
            </ul>

            {hasNext ? (
              <div className="bands-page__more">
                <button
                  className="bands-page__more-btn"
                  disabled={loadingMore}
                  onClick={handleLoadMore}
                  type="button"
                >
                  {loadingMore ? '로딩 중...' : '더 보기'}
                </button>
              </div>
            ) : null}
          </>
        )}
      </section>

      <HomeFooter />
    </main>
  );
}
